/**
 * usePermissions Hook (Phase 1 RBAC Bridge)
 *
 * Reads resolved permissions from /api/auth/me response.
 *
 * IMPORTANT: This hook uses a module-level singleton cache + in-flight
 * request dedup so that multiple components mounted simultaneously
 * (e.g. 25 RowActions rows in a table) share ONE network call.
 *
 * Without this, each row would fire its own /auth/me fetch and the
 * action buttons would pop in independently as each request resolves,
 * causing visible layout shifts.
 *
 * Usage:
 *   const { hasPermission, hasAnyPermission, permissions, loading } = usePermissions();
 *   if (hasPermission('orders', 'assign')) { ... }
 */
import { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

// ---------- Module-level singleton state ----------
let cachedPermissions = null;        // null = not yet loaded, [] = loaded but empty
let inflightPromise = null;          // dedup concurrent fetches
let cachedToken = null;              // bust cache when token changes
const subscribers = new Set();       // setState callbacks from active hook instances

const notify = () => {
  subscribers.forEach((cb) => cb(cachedPermissions));
};

const fetchPermissionsOnce = (token) => {
  // If token changed (login/logout/impersonation), invalidate cache
  if (cachedToken !== token) {
    cachedToken = token;
    cachedPermissions = null;
    inflightPromise = null;
  }

  if (cachedPermissions !== null) {
    return Promise.resolve(cachedPermissions);
  }

  if (inflightPromise) {
    return inflightPromise;
  }

  inflightPromise = (async () => {
    try {
      if (!token) {
        cachedPermissions = [];
        return cachedPermissions;
      }

      const response = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        cachedPermissions = [];
        return cachedPermissions;
      }

      const userData = await response.json();
      cachedPermissions =
        Array.isArray(userData?.permissions) ? userData.permissions : [];
      return cachedPermissions;
    } catch {
      cachedPermissions = [];
      return cachedPermissions;
    } finally {
      inflightPromise = null;
      notify();
    }
  })();

  return inflightPromise;
};

// WORKSPACE-SWITCHER-001: resolve permissions once (login redirect) without a hook.
export const loadPermissions = (token) => fetchPermissionsOnce(token);

// Allow external code (e.g. logout) to clear the cache.
export const clearPermissionsCache = () => {
  cachedPermissions = null;
  cachedToken = null;
  inflightPromise = null;
  notify();
};

export const usePermissions = () => {
  const token =
    typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const [permissions, setPermissions] = useState(
    cachedPermissions !== null && cachedToken === token ? cachedPermissions : []
  );
  const [loading, setLoading] = useState(
    !(cachedPermissions !== null && cachedToken === token)
  );

  useEffect(() => {
    let cancelled = false;

    const subscriber = (perms) => {
      if (cancelled) return;
      setPermissions(perms || []);
      setLoading(false);
    };
    subscribers.add(subscriber);

    // If cache is already populated for this token, sync immediately
    if (cachedPermissions !== null && cachedToken === token) {
      setPermissions(cachedPermissions);
      setLoading(false);
    } else {
      fetchPermissionsOnce(token).then((perms) => {
        if (cancelled) return;
        setPermissions(perms || []);
        setLoading(false);
      });
    }

    return () => {
      cancelled = true;
      subscribers.delete(subscriber);
    };
  }, [token]);

  const hasPermission = useCallback(
    (resource, action) =>
      permissions.some((p) => p.resource === resource && p.action === action),
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (permissionList) =>
      permissionList.some((perm) => {
        if (typeof perm === 'string') {
          const [resource, action] = perm.split(':');
          return hasPermission(resource, action);
        }
        return hasPermission(perm.resource, perm.action);
      }),
    [hasPermission]
  );

  return {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
  };
};

export default usePermissions;
